import { computed, ref, watch } from 'vue';
import { ERROR_REQUIRED } from '@/constants/validation/error-messages';
import { validateDateOfBirth, validatePartialNric } from '@/utils/registration-validation';
import { VERIFY_ERROR_KINDS } from '@/utils/giving-login';

const UNKNOWN_ERROR = Object.freeze({
  kind: VERIFY_ERROR_KINDS.UNKNOWN,
  message: 'Something went wrong. Please try again.',
  canRetry: true,
  offerSupport: false,
});

const pad = n => String(n).padStart(2, '0');

/** Today as `yyyy-mm-dd` in the device's calendar: the latest date of birth allowed. */
function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function partialNricErrorOf(value) {
  if (!value || !value.trim()) return ERROR_REQUIRED;
  return validatePartialNric(value) || '';
}

/**
 * The one-time personal-id check's form (giving per login, @/utils/giving-login):
 * date of birth + the last 4 characters of the NRIC, the profile's rules, and the
 * mapped error of the last attempt. Shared by MyGiving's VerifyPersonalIdCard and My
 * QR, which differ only in which API `verify` asks.
 *
 * @param {object} options
 * @param {(fields: { partialNric: string, dateOfBirth: string }) => Promise<object>} options.verify
 *   resolves `{ ok: true, ... }` or `{ ok: false, error }`, never rejects
 * @param {(result: object) => void} options.onVerified called once the server accepts them
 */
export function useVerifyPersonalIdForm({ verify, onVerified }) {
  const dateOfBirth = ref('');
  const partialNric = ref('');
  const touched = ref(false);
  const submitting = ref(false);
  const error = ref(null);

  const today = computed(todayIso);
  const dateOfBirthError = computed(() => validateDateOfBirth(dateOfBirth.value) || '');
  const partialNricError = computed(() => partialNricErrorOf(partialNric.value));
  const isValid = computed(() => !dateOfBirthError.value && !partialNricError.value);
  // Out of attempts, rate limited, or nothing to check against: retrying cannot help.
  const isBlocked = computed(() => Boolean(error.value && !error.value.canRetry));

  // A new value is a new attempt: the last mismatch no longer describes it.
  const clearRetryableError = () => {
    if (error.value?.canRetry) error.value = null;
  };
  watch([dateOfBirth, partialNric], clearRetryableError);

  const reset = () => {
    error.value = null;
    partialNric.value = '';
    dateOfBirth.value = '';
    touched.value = false;
  };

  const submit = async () => {
    if (submitting.value || isBlocked.value) return;
    touched.value = true;
    if (!isValid.value) return;

    submitting.value = true;
    try {
      const result = await verify({
        partialNric: partialNric.value,
        dateOfBirth: dateOfBirth.value,
      });
      if (result?.ok) {
        reset();
        onVerified(result);
        return;
      }
      error.value = result?.error ?? { ...UNKNOWN_ERROR };
    } finally {
      submitting.value = false;
    }
  };

  return {
    dateOfBirth,
    partialNric,
    touched,
    submitting,
    error,
    today,
    dateOfBirthError,
    partialNricError,
    isValid,
    isBlocked,
    submit,
    VERIFY_ERROR_KINDS,
  };
}
